import { useState } from 'react'
import { motion } from 'framer-motion'
import { Button, Card, Input, StatusPill } from '../components/primitives/index.js'
import SEO from '../components/SEO.jsx'
import { api } from '../lib/api.js'
import './TrackOrderPage.css'

/** Shared status metadata + building blocks — reused by AccountOrders so
 * order status/shipment rendering stays identical between the two pages.
 * Colors mirror Prod's admin STATUS_PILL palette (src/pages/admin/ui.jsx)
 * exactly, so status coloring is consistent between admin and customer views. */
export const STATUS_META = {
  pending: { label: 'Pending', description: 'Order received — awaiting payment confirmation.', color: '#facc15' },
  paid: { label: 'Paid', description: 'Payment confirmed — preparing for fulfillment.', color: '#4ade80' },
  partial: { label: 'Partially shipped', description: 'Part of this order has shipped; the rest is on the way.', color: '#fb923c' },
  fulfilled: { label: 'Fulfilled', description: 'Your order has shipped.', color: '#60a5fa' },
  issue: { label: 'Order Issue', description: 'There’s an issue with your order that our team is reviewing. We’ll follow up with details shortly.', color: '#c084fc' },
  cancelled: { label: 'Cancelled', description: 'This order was cancelled.', color: '#f87171' },
  complete: { label: 'Delivered', description: 'Delivered — thanks for your order.', color: '#a3a3a3' },
  delivered: { label: 'Delivered', description: 'Delivered — thanks for your order.', color: '#a3a3a3' },
  processing: { label: 'Processing', description: 'Preparing your order for shipment.', color: '#facc15' },
  shipped: { label: 'Shipped', description: 'Your order is on the way.', color: '#60a5fa' },
}

const TRACK_STATUS_META = {
  pre_transit: 'Label created',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  exception: 'Delivery exception',
}

export function fmtDateTime(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

export function ShipmentCard({ shipment }) {
  const statusLabel = TRACK_STATUS_META[shipment.status] ?? shipment.status
  return (
    <Card className="track-order__shipment">
      <div className="track-order__shipment-top">
        <span className="track-order__shipment-carrier">{shipment.carrier} &middot; {shipment.number}</span>
        <StatusPill label={statusLabel} color={shipment.status === 'delivered' ? '#a3a3a3' : '#60a5fa'} />
      </div>
      {shipment.items?.length > 0 && (
        <p className="track-order__shipment-items">{shipment.items.map((i) => `${i.name} ×${i.qty}`).join(', ')}</p>
      )}
      <div className="track-order__shipment-meta">
        {shipment.deliveredAt ? <span>Delivered {fmtDateTime(shipment.deliveredAt)}</span> : shipment.eta ? <span>ETA {fmtDateTime(shipment.eta)}</span> : null}
        {shipment.url && (
          <a href={shipment.url} target="_blank" rel="noopener noreferrer" className="track-order__shipment-link">
            Track your package &rarr;
          </a>
        )}
      </div>
    </Card>
  )
}

export function Timeline({ history }) {
  if (!history?.length) return null
  return (
    <div className="track-order__timeline">
      {history.map((h, i) => {
        const meta = STATUS_META[h.status]
        return (
          <div key={i} className="track-order__timeline-row">
            <span className="track-order__timeline-dot" style={{ background: meta?.color ?? '#a3a3a3' }} />
            <span className="track-order__timeline-status">{meta?.label ?? h.status}</span>
            <span className="track-order__timeline-date">{fmtDateTime(h.at)}</span>
          </div>
        )
      })}
    </div>
  )
}

export default function TrackOrderPage() {
  const [email, setEmail] = useState('')
  const [invoice, setInvoice] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!email.trim() || !invoice.trim() || loading) return
    setLoading(true)
    setError('')
    try {
      const data = await api.trackOrder(email.trim(), invoice.trim())
      setResult(data)
    } catch (err) {
      setResult(null)
      setError(err.message ?? 'Lookup failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const meta = result ? (STATUS_META[result.status] ?? { label: result.status, description: '' }) : null

  return (
    <div className="container track-order">
      <SEO config={{ title: 'Track Your Order', robots: 'noindex, nofollow', titleRaw: true }} />
      <h1>Track your order</h1>
      <p className="track-order__subtitle">Enter the email and invoice number from your order confirmation.</p>

      <Card className="track-order__card">
        <form onSubmit={handleSubmit} className="track-order__form">
          <Input label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Invoice number" placeholder="LL-10482" required value={invoice} onChange={(e) => setInvoice(e.target.value)} />
          {error && <p className="track-order__error">{error}</p>}
          <Button type="submit" variant="primary" size="lg" loading={loading} className="track-order__submit">
            Track order
          </Button>
        </form>
      </Card>

      {result && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Card className="track-order__result">
            <div className="track-order__result-header">
              <h3>Invoice {result.invoiceNumber}</h3>
              <StatusPill label={meta.label} color={meta.color ?? '#a3a3a3'} />
            </div>
            {meta.description && <p className="track-order__result-desc">{meta.description}</p>}

            {result.shipments?.map((s, i) => (
              <ShipmentCard key={i} shipment={s} />
            ))}

            <Timeline history={result.history} />

            {result.trackingNumber && !result.shipments?.length && (
              <p className="track-order__tracking">
                {result.shippingCarrier} tracking: <strong>{result.trackingNumber}</strong>
              </p>
            )}
          </Card>
        </motion.div>
      )}
    </div>
  )
}
